/**
 * "AI is working" star sequence.
 *
 * Four stacked star SVGs cycle one at a time via a pure CSS keyframe
 * (`.ai-working-seq` in globals.css): each star is visible for 25% of the
 * cycle, offset with animation-delay, so exactly one star shows at a time
 * and the shape changes in sequence. No JS timer — keeps animating behind
 * modal backdrops and while the main thread is busy.
 *
 * Heroicons only ships SparklesIcon + StarIcon, so the variants are custom
 * inline paths (24x24 viewBox, currentColor).
 *
 * Mount this component when work STARTS (conditional render) — Safari does
 * not reliably start a CSS animation when a class is added to an existing
 * element, but mounting WITH the class always works.
 */

const CYCLE_S = 2.4;
const STAR_COUNT = 4;
const STEP_S = CYCLE_S / STAR_COUNT;

// Classic concave 4-point sparkle
const SPARKLE =
  'M12 2C12.6 8 16 11.4 22 12C16 12.6 12.6 16 12 22C11.4 16 8 12.6 2 12C8 11.4 11.4 8 12 2Z';

// Tall thin glint (narrow 4-point)
const GLINT =
  'M12 1C12.4 8.5 13.5 11.6 17 12C13.5 12.4 12.4 15.5 12 23C11.6 15.5 10.5 12.4 7 12C10.5 11.6 11.6 8.5 12 1Z';

const STARS: React.ReactNode[] = [
  // 1. Sparkle
  <path key="sparkle" d={SPARKLE} />,
  // 2. Diagonal sparkle (same motif rotated 45° — an X-shaped twinkle)
  <g key="diagonal" transform="rotate(45 12 12) scale(0.92) translate(1 1)">
    <path d={SPARKLE} />
  </g>,
  // 3. Twinkle: big sparkle + two small companions (SparklesIcon motif)
  <g key="twinkle">
    <g transform="translate(0 5) scale(0.68)">
      <path d={SPARKLE} />
    </g>
    <g transform="translate(13 0) scale(0.42)">
      <path d={SPARKLE} />
    </g>
    <g transform="translate(16 13) scale(0.3)">
      <path d={SPARKLE} />
    </g>
  </g>,
  // 4. Thin glint
  <path key="glint" d={GLINT} />,
];

export default function AiWorkingStar({ className }: { className?: string }) {
  return (
    <span className={`relative inline-block ${className ?? ''}`} aria-hidden="true">
      {STARS.map((star, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          fill="currentColor"
          className="ai-working-seq absolute inset-0 h-full w-full"
          style={{ animationDelay: `${i * STEP_S}s` }}
        >
          {star}
        </svg>
      ))}
    </span>
  );
}
